import { Head, Link, router } from '@inertiajs/react'
import { Suspense, lazy, memo, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { chromeInsets, type Insets, type PlaceCamera } from '../lib/camera'
import { CHROME_FOCUS_CLASS, SIGN_IN_PATH, type ChromePopover } from '../lib/chrome_config'
import {
  COUNTRY_KIND,
  INITIAL_ZOOM,
  JOURNAL_PATH,
  MAP_ROOT_PATH,
  PANEL_PAGES,
  PANEL_PREFETCH_CACHE_MS,
  PANEL_PREFETCH_DWELL_MS,
  PANEL_WARM_WAIT_MS,
  PHONE_MEDIA_QUERY,
  PLAIN_BACKGROUND_COLOR,
  REGION_FEATURE_CAP,
  REGION_FETCH_DEBOUNCE_MS,
  REGION_PENDING_DELAY_MS,
  SOURCES_PATH,
  SPOTLIGHT_PATH,
  WINERY_ANSWER_GRACE_MS,
  WINERY_CAMERA_ZOOM,
  WINERY_PENDING_DELAY_MS,
  WISHLIST_PATH,
  placePath,
  tastingPath,
  viewportBbox,
  viewportKey,
  viewportZoom,
} from '../lib/map_config'
import BasemapAttribution from '../components/map/basemap_attribution'
import {
  MapFilteredContext,
  PanelCloseContext,
  PanelEnteredAtContext,
  PanelEnteringContext,
} from '../components/map/detail_panel'
import HoverLabel, { type HoverLabelHandle } from '../components/map/hover_label'
import LayerToggles from '../components/map/layer_toggles'
import MapBoundary from '../components/map/map_boundary'
import MapUnavailable from '../components/map/map_unavailable'
import {
  PanelNavigationContext,
  panelKey,
  panelPath,
  type PanelNavigation,
  type PanelTarget,
} from '../components/map/panel_navigation'
import PendingPanel from '../components/map/pending_panel'
import PlateRibbon from '../components/map/plate_ribbon'
import SearchBox from '../components/search/search_box'
import WineryFilters from '../components/map/winery_filters'
import type { HoverAbout } from '../lib/hover'
import type { MapReach } from '../lib/locate'
import { warmPages } from '../lib/page_registry'
import { declineRateLimitDialog, isRateLimited } from '../lib/rate_limit'
import { RegionMemory, frameCovers, frameOf, mergeRegionFeatures, type RegionFrame } from '../lib/region_frames'
import useLate from '../lib/use_late'
import useLayerFetch from '../lib/use_layer_fetch'
import {
  EMPTY_WINERY_FILTERS,
  activeFilterCount,
  canonicalRequest,
  echoKey,
  requestData,
  requestKey,
  wineryViewportOf,
} from '../lib/winery_viewport'
import type { MapStatus } from '../components/map/use_maplibre'
import type {
  AtlasLayer,
  AtlasProps,
  DetailFeature,
  DetailLayer,
  MapViewport,
  PlaceDetail,
  WineryDetail,
  WineryFilters as WineryFilterState,
  WineryLayer,
} from '../types/atlas'
import type { SharedProps } from '../types'

// Dynamic, so maplibre-gl is route-split out of the entry bundle and never
// evaluated during a server render. A static import here would also pull the
// library into ssr/ssr.tsx, which globs every page eagerly.
//
// STARTED WHEN THIS MODULE IS EVALUATED, not when React first renders the
// `lazy` below. That render is several steps later — Inertia resolves the page,
// React mounts the shell, Suspense meets the lazy component — and the map chunk
// is the largest download on the page, so every one of those steps was time it
// could have been arriving in. The promise is made once and handed to `lazy`,
// which is all `lazy` ever wanted.
//
// The `typeof window` guard is the server render: ssr/ssr.tsx evaluates every
// page module eagerly, this one included, and must not start importing a
// library that touches `window` at evaluation. There the import is left to
// `lazy`, which a server render never resolves.
//
// The `catch` handles nothing. A chunk that fails to load is reported by the
// `MapBoundary` around the lazy component, which receives the same rejection;
// this only stops the browser also logging it as unhandled in the moment
// between the import failing and React rendering the component that asked.
const loadAtlasMap = () => import('../components/map/atlas_map')
const atlasMapChunk = typeof window === 'undefined' ? null : loadAtlasMap()
atlasMapChunk?.catch(() => {})

const AtlasMap = lazy(() => atlasMapChunk ?? loadAtlasMap())

/**
 * The `Suspense` the map waits in, and the one update that mounts the map THE
 * MOMENT ITS CHUNK HAS EVALUATED rather than when React gets round to it.
 *
 * A lazy component suspends the first time it is rendered — always, even when
 * its import settled long before — so the shell's first commit is the empty
 * fallback and the map arrives in a retry. And a render made of nothing but
 * Suspense retries is one react-dom holds back: it is not committed until
 * 300 ms after the fallback was (its retry throttle, there so that a page of
 * boundaries resolving one after another does not repaint for each). What
 * ended the wait any sooner was whichever ORDINARY update came first, which on
 * a hard load was the deferred country layer landing. Measured: the map was
 * built 200 to 225 ms after the shell's first commit although its chunk had
 * evaluated about 40 ms after it, with the main thread idle for half of that
 * wait — and where the countries took longer to land, at 300.
 *
 * So the ordinary update is made here, when the import settles. Its render is
 * not a retry and is not throttled: it finds `lazy` resolved and commits at
 * once. Settles EITHER WAY — a chunk that failed is thrown by that same render
 * into the `MapBoundary` above, and the readable atlas is not held back
 * either. The map is therefore usually built before the countries have
 * landed, which is the order a slow connection always had: they reach it as
 * data for a source that is already there (`use_maplibre.ts`).
 *
 * TWO SMALL COMPONENTS, FOR WHAT EACH ONE SAVES. The state is in a component
 * of its own directly above the boundary because that is the least that can
 * hold it: React tries a boundary's content again only in a render that
 * reaches the boundary from above — an update inside the fallback renders the
 * fallback — and held in the shell the same update would render the whole
 * chrome a moment before the map is built. And the listening is done by the
 * FALLBACK, which exists only while the map is really waiting: a shell
 * mounted once the chunk is in (back from the journal; every render in a test
 * file after its first) does not suspend, never mounts it, and makes no
 * update at all.
 */
function MapSuspense({ children }: { children: ReactNode }) {
  const [, setAttempt] = useState(0)
  const retry = useCallback(() => setAttempt((attempt) => attempt + 1), [])

  return <Suspense fallback={<MapChunkWait onSettled={retry} />}>{children}</Suspense>
}

// The fallback. It draws nothing, as the `null` it replaces drew nothing (the
// reason is where `MapSuspense` is used), and says when the import has settled.
function MapChunkWait({ onSettled }: { onSettled: () => void }) {
  useEffect(() => {
    atlasMapChunk?.then(onSettled, onSettled)
  }, [onSettled])

  return null
}

// "Show my location" — the button, the question it asks the browser, the mark
// and the flight — is a chunk of its own too, and the opposite of the map's in
// when it is asked for: NOT at module evaluation, not at first render, but
// after the map has drawn and the browser has gone idle (`Locate`, below).
// Most readers never press it, and nobody should wait on it.
const LocateControl = lazy(() => import('../components/map/locate_control'))

interface AtlasLayoutProps extends AtlasProps {
  children: ReactNode
  /**
   * The shared prop every Inertia page carries, which a layout receives along
   * with the page's own. Optional here although the server always sends it: a
   * component test renders this layout with map props alone, and "no word from
   * the server" must read as the signed-in chrome the app has always had rather
   * than as a visitor's.
   */
  auth?: SharedProps['auth']
}

const featureMap = (layer?: DetailLayer) =>
  new Map((layer?.features ?? []).map((feature) => [feature.properties.slug, feature]))

/**
 * Where the camera goes for an open place.
 *
 * A place with a shape of its own is fitted or flown to as before. A place with
 * neither — 353 of the appellations P5 imported, because no open source
 * publishes a Chilean DO's boundary or even its centroid — flies to the nearest
 * ancestor that has a point, AT THAT ANCESTOR'S ZOOM. The zoom is the whole
 * point: flying to the middle of Chile at `PLACE_FLY_ZOOM` would show a reader
 * one vineyard's worth of ground and imply the appellation is there.
 */
function cameraFor(place: PlaceDetail): PlaceCamera {
  if (place.bbox || place.point) return { slug: place.slug, bbox: place.bbox, point: place.point }
  if (!place.located_via) return { slug: place.slug, bbox: null, point: null }

  return {
    slug: place.slug,
    bbox: null,
    point: place.located_via.point,
    zoom: place.located_via.zoom,
  }
}

/**
 * Where the camera goes for an open producer, if anywhere.
 *
 * A winery page used to ship no camera at all, on the reasoning that the reader
 * had already chosen a viewport. That holds for a click on a dot and for
 * nothing else: opened by its URL, or picked from the search box at the world
 * view, the panel described an estate beside a map of five continents. So a
 * winery now has a camera — its own point, at `WINERY_CAMERA_ZOOM` — and the
 * old reasoning survives as `visibleFrom`: the hook leaves the camera alone
 * when the point is already a dot in plain view, and only slides it out from
 * under the panel when the panel is what hid it.
 *
 * The slug is prefixed because a producer and a place can share one (KTD15),
 * and the hook moves when the camera's key changes: Hanzell the winery opened
 * from Hanzell the hypothetical appellation must still be a move.
 *
 * Null for a producer with no coordinates, which resolved to no place at all
 * and whose panel says so in words.
 */
function wineryCamera(winery: WineryDetail, visibleFrom: number): PlaceCamera | null {
  if (!winery.point) return null

  return { slug: `winery:${winery.slug}`, bbox: null, point: winery.point, zoom: WINERY_CAMERA_ZOOM, visibleFrom }
}

type RegionFetch = 'idle' | 'pending' | 'ready' | 'failed' | 'dismissed'

// One appellation request in flight: the ground it will deliver, the frame the
// camera was in when it was made (which is what a dismissed failure is keyed
// on), whether that ground includes a margin, the page it was made from, and
// whether anything has answered it yet.
interface RegionRequest {
  frame: RegionFrame
  viewKey: string
  padded: boolean
  from: string
  answered: boolean
}

// See the `pending` state in the layout. `held` is how tall the sheet on screen
// was when the stand-in took its place, for the stand-in to keep (see
// `PendingPanel`); null when there was none.
type PendingPanelState =
  | { kind: 'open'; target: PanelTarget; failed: boolean; held: number | null }
  | { kind: 'close' }

const currentPath = () => window.location.pathname

// Whether the page a reload's `onSuccess` was handed is a different page from
// the one the reload was made on. A stand-in for Inertia's page in a test
// carries no `url`, and reads as the same page.
function wentElsewhere(page: unknown, from: string) {
  const url = (page as { url?: unknown }).url
  if (typeof url !== 'string') return false

  return new URL(url, window.location.origin).pathname !== from
}

/**
 * Fills in what the client already knows about something a reader chose, so
 * its panel can be named before its page arrives.
 *
 * The map reports a click as a slug and nothing else, and it does not need to
 * report more: an appellation's name is on the feature the shell is holding, a
 * country's is in the world layer, a producer's is in the winery layer, and a
 * search result's is in the `search` prop that was on screen when it was
 * picked. A caller that already has the name — a breadcrumb, a child row —
 * passes it and is believed.
 *
 * And whether the map has a mark for it, which decides whether the stand-in's
 * kind line opens with one (`PanelTarget.onMap`). That is looked up even for a
 * caller that brought its own name, because a breadcrumb knows what a place is
 * called and not whether it has a shape: a place the shell holds a feature
 * for has one, a producer the winery layer is drawing has a dot — and, for
 * that producer, which dot. Anything else is left unsaid rather than guessed.
 */
function describe(
  target: PanelTarget,
  regions: Map<string, DetailFeature>,
  world: AtlasProps['world_layer'],
  search: AtlasProps['search'],
  wineries: WineryLayer | null,
): PanelTarget {
  const bySlug = <Row extends { slug: string }>(rows: readonly Row[] | undefined) =>
    rows?.find((row) => row.slug === target.slug)

  if (target.type === 'place') {
    const drawn =
      regions.get(target.slug)?.properties ??
      world?.features.find((feature) => feature.properties.slug === target.slug)?.properties
    const marked = drawn ? { ...target, onMap: true } : target
    if (target.name) return marked

    const found = drawn ?? bySlug(search?.places)

    return found ? { ...marked, name: found.name, kind: found.kind } : marked
  }

  if (target.type === 'winery') {
    const dot = bySlug(wineries?.wineries)
    const marked = dot
      ? { ...target, onMap: true, dot: { sizeClass: dot.size_class, stated: dot.size_stated } }
      : target
    if (target.name) return marked

    const found = dot ?? bySlug(search?.wineries)

    return found ? { ...marked, name: found.name } : marked
  }

  if (target.name) return target

  const found = bySlug(search?.grapes)

  return found ? { ...target, name: found.name } : target
}

// How many wineries the layer is actually drawing, whichever mode it is in. In
// cluster mode that is the sum of the cells' counts, not the number of cells —
// "showing 40 of 9,000" would be counting the wrong thing at the reader.
function drawnWineries(layer: WineryLayer): number {
  return layer.mode === 'points'
    ? layer.wineries.length
    : layer.clusters.reduce((sum, cluster) => sum + cluster.count, 0)
}

// A nav link is the pill's full height, because that is the target: 44px under
// a thumb and 40px under a pointer. Its hover tint is drawn 4px inside that on
// a pseudo-element, so it reads as a chip within the pill rather than as a
// block filling it. `isolate` gives the `-z-10` tint a stacking context to sit
// at the bottom of, under the word.
const NAV_LINK_CLASS =
  'relative isolate inline-flex h-11 items-center rounded-full px-2.5 text-[13px] font-medium md:h-10 ' +
  'before:absolute before:inset-x-0 before:inset-y-1 before:-z-10 before:rounded-full hover:before:bg-black/5 ' +
  'focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-accent md:text-sm'

// The notice stack's two skins. Neutral is the map describing itself — what it
// is showing, what it is fetching — in the ink, so it reads against land and
// water alike. Warning is a failure with something to do about it: the chrome's
// paper with a claret rule down its leading edge. It was an amber card, and on
// this plate amber is the selection's and nothing else's — the banner used to
// come up beside the amber outline of the place whose wineries had failed.
//
// The neutral pill is `rounded-2xl`, not `rounded-full`: on one line the two
// look the same, and the truncation sentence wraps to two on a phone, where a
// full radius would turn the pill into a lozenge with its text in the corners.
// It is transparent to the pointer unless the notice opts in, because most of
// them have nothing to press and the map beneath should still drag. Vertical
// padding is left to each notice for the same reason: one that carries a button
// is already as tall as the button's 36px target and needs almost none.
const NEUTRAL_NOTICE_CLASS = 'rounded-2xl bg-ink/90 px-3.5 text-xs text-white shadow-chrome'
const WARNING_NOTICE_CLASS =
  'pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl border-l-4 border-accent bg-surface-raised ' +
  'px-4 py-2.5 text-sm text-ink shadow-chrome ring-1 ring-black/10'
// The focus ring takes the notice's own text colour rather than the accent: one
// of the two skins is the ink, and claret on wine-black is a ring nobody sees.
const NOTICE_BUTTON_CLASS =
  'min-h-9 rounded font-medium underline underline-offset-2 focus-visible:outline-2 ' +
  'focus-visible:outline-offset-2 focus-visible:outline-current'

interface LocateProps {
  /** The map has reported a style: there is something to move. */
  ready: boolean
  mapHandle: { current: MapReach | null }
  insets: () => Insets
  plain: () => boolean
  home: boolean
  stack: { current: HTMLElement | null }
}

const quietly = () => {}

/**
 * Where the locate button will be, and when it arrives.
 *
 * Everything the button is and does lives in `LocateControl`'s chunk. What is
 * here — and so what every map page carries for the feature before a reader
 * has seen a map — is the decision of WHEN that chunk is asked for: once the
 * map has drawn its first frame of tiles (its first `idle`) and the browser
 * then says it has nothing better to do. Not at "ready", which is the style
 * having loaded with the tiles still on their way; nothing of this is to be in
 * front of them. `PANEL_WARM_WAIT_MS` is the longest that second wait runs,
 * for the reason it bounds the panel pages' warm-up.
 *
 * A COMPONENT OF ITS OWN FOR ITS ONE PIECE OF STATE. "The control is wanted
 * now" renders these few lines and mounts the control; held in the shell it
 * would render the shell, on every map page, a moment after the map had drawn.
 * And memoised, for the other direction: every prop but `ready` is the same
 * value for the life of the shell, so the shell's own renders — a camera
 * settle, a popover, a panel — do not reach the button at all.
 *
 * The boundary is for the chunk failing to load, which is what a rejected
 * `lazy` import does to a tree with nothing to catch it (`MapBoundary` has the
 * whole story). Here the right outcome is simply no button.
 *
 * Nothing at all for a map that never came up: `ready` stays false, and there
 * is nothing for a button to move.
 */
const Locate = memo(function Locate({ ready, mapHandle, ...lent }: LocateProps) {
  const [wanted, setWanted] = useState(false)

  useEffect(() => {
    const map = mapHandle.current?.map
    if (!ready || !map) return

    let live = true
    const want = () => {
      const mount = () => {
        if (live) setWanted(true)
      }

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(mount, { timeout: PANEL_WARM_WAIT_MS })
      } else {
        // Safari. The map has already said it is idle, which is most of the point.
        setTimeout(mount, 0)
      }
    }

    if (map.loaded()) want()
    else map.once('idle', want)

    return () => {
      live = false
    }
  }, [ready, mapHandle])

  if (!wanted || !ready) return null

  return (
    <MapBoundary onError={quietly}>
      <Suspense fallback={null}>
        <LocateControl
          mapHandle={mapHandle}
          focusClassName={CHROME_FOCUS_CLASS}
          noticeClassName={NEUTRAL_NOTICE_CLASS}
          noticeButtonClassName={NOTICE_BUTTON_CLASS}
          {...lent}
        />
      </Suspense>
    </MapBoundary>
  )
})

interface AtlasListProps {
  world: AtlasProps['world_layer']
  /** The map could not be built, and this list is what is left of the atlas. */
  failed: boolean
  /** A place panel is open, so a link from here replaces it rather than stacking. */
  replace: boolean
}

const ATLAS_LIST_ID = 'atlas-list'

/**
 * Every country, as links.
 *
 * Always in the DOM, never only on failure. A canvas is unreachable by keyboard
 * and silent to a screen reader, so this list is the atlas's only readable path
 * — and the same markup is the WebGL2 fallback. As the fallback it starts below
 * the failure notice, which is pinned under the header (see `MapUnavailable`):
 * two rows of header and a notice that wraps on a phone, one row and a shorter
 * notice from `md`.
 *
 * IN THE DOCUMENT, AND OUT OF THE TAB ORDER UNTIL IT IS OPENED. While the map
 * is up the list is drawn for nobody, and it used to be 237 tab stops that
 * were also drawn for nobody: after the last control on the page, a reader on
 * a keyboard pressed Tab up to 237 times with nothing on screen showing where
 * they were — the one place in the chrome where focus could not be seen, on
 * every map page. So there is ONE stop in its place, a button that says what
 * it opens and is visible whenever it has focus, and the links take no focus
 * of their own until it has been pressed. Pressed, the list is a paper sheet
 * over the map, focus goes to its first country, and Escape or its Close puts
 * both back. A screen reader still finds every link where it always was:
 * `tabindex="-1"` takes a link out of the Tab sequence and out of nothing
 * else.
 *
 * A COMPONENT OF ITS OWN, AND MEMOISED, for what it costs rather than for what
 * it is. It is 237 links nobody looking at the map can see, and it used to be
 * rebuilt on every render of the shell — every camera settle, every layer that
 * arrived, every popover — as most of the work of each one. The shell now
 * renders on a click as well, to show a panel's frame before its page lands,
 * and that render is the one a reader waits on. With three props that change
 * only when the country layer arrives, the map fails or a place opens, the
 * list is built about once a visit — and once more when it is opened, which
 * is a reader asking for it.
 */
const AtlasList = memo(function AtlasList({ world, failed, replace }: AtlasListProps) {
  const [open, setOpen] = useState(false)
  const opener = useRef<HTMLButtonElement>(null)
  const list = useRef<HTMLElement>(null)
  const shown = failed || open

  // Opened: focus goes into the list, to its first country — or to the list
  // itself while the countries are still on their way.
  useEffect(() => {
    if (!open) return

    const first = list.current?.querySelector<HTMLElement>('a')
    ;(first ?? list.current)?.focus()
  }, [open])

  const close = () => {
    setOpen(false)
    opener.current?.focus()
  }

  return (
    <>
      {!failed && (
        <button
          ref={opener}
          type="button"
          data-testid="atlas-list-open"
          aria-expanded={open}
          aria-controls={ATLAS_LIST_ID}
          onClick={() => setOpen((was) => !was)}
          // Drawn only while it has focus: a reader with a pointer has the map,
          // and a reader with a keyboard meets it exactly when they need it.
          className={`pointer-events-none absolute bottom-14 left-4 z-50 inline-flex h-11 items-center rounded-full bg-surface-raised px-4 text-sm font-medium text-ink opacity-0 shadow-chrome ring-1 ring-black/5 focus-visible:pointer-events-auto focus-visible:opacity-100 ${CHROME_FOCUS_CLASS}`}
        >
          Browse countries as a list
        </button>
      )}

      <nav
        ref={list}
        id={ATLAS_LIST_ID}
        aria-label="Atlas"
        tabIndex={-1}
        // A press on a country is a visit; the sheet has done its job.
        onClick={(event) => {
          if (open && (event.target as HTMLElement).closest('a')) setOpen(false)
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || !open) return

          // The detail panel listens for Escape on `document`; a reader
          // putting the list away has not asked to close the place.
          event.stopPropagation()
          close()
        }}
        className={
          failed
            ? 'absolute inset-0 z-10 overflow-y-auto bg-surface px-6 pb-24 pt-80 md:pt-56'
            : open
              ? 'absolute inset-x-3 bottom-3 top-20 z-50 overflow-y-auto overscroll-contain rounded-2xl bg-surface-raised p-5 shadow-panel ring-1 ring-black/5 focus:outline-none md:inset-x-auto md:left-4 md:w-[min(44rem,calc(100%-2rem))]'
              : 'sr-only'
        }
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-[17px] font-semibold">Countries</h2>
          {open && (
            <button
              type="button"
              onClick={close}
              className={`inline-flex h-11 items-center rounded-full px-3 text-sm font-medium text-accent hover:bg-black/[0.04] ${CHROME_FOCUS_CLASS}`}
            >
              Close
            </button>
          )}
        </div>
        {/* `world_layer` is deferred: absent from the document, present a request
            later (and from then on for the life of the visit — it is `once`). The
            list is the atlas's only readable path, so while its data is still on
            the way it says so rather than reading as an atlas with no countries
            in it. */}
        {!world && (
          <p data-testid="atlas-list-pending" role="status" className="mt-3 text-sm text-ink-soft">
            Loading the countries…
          </p>
        )}
        <ul className="mt-3 grid gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {(world?.features ?? []).map((feature) => (
            <li key={feature.properties.slug}>
              <Link
                href={placePath(feature.properties.slug)}
                replace={replace}
                tabIndex={shown ? undefined : -1}
                className={`rounded-sm text-sm text-accent underline decoration-accent/30 underline-offset-2 hover:decoration-accent ${CHROME_FOCUS_CLASS}`}
              >
                {feature.properties.name}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </>
  )
})

/**
 * The persistent layout every map route resolves to.
 *
 * Inertia reads a static `layout` property off the page component and wraps the
 * page with `reduceRight`; the page element is created with a key and the layout
 * element without one, so React preserves this instance and remounts only the
 * keyed child. That is what keeps one MapLibre instance alive across a visit to
 * a place and back, instead of destroying and rebuilding it on every click.
 *
 * It depends on both pages resolving to *this* component — a wrapper created per
 * page would type check and silently rebuild the map.
 *
 * Surviving navigation is also why the fetched region layer is held here in
 * state: `detail_layer` is an `optional` prop, so it is not re-sent on the next
 * visit, and a place click would otherwise blank the regions the reader just
 * zoomed in to see.
 *
 * Under KTD5 that held layer is also the MERGE SUBSTRATE. `detail_layer` serves
 * one camera frame, and the union of every frame this shell has visited lives
 * here in React state and is NEVER written back into the Inertia page object.
 * That is deliberate and load-bearing: Inertia writes the page object into
 * `window.history.state` on every navigation, where an overflow is swallowed as
 * an internal `historyQuotaExceeded` event rather than raised — a silently
 * broken Back button. Keeping the union out of it means history carries only the
 * most recent frame's prop, Back re-requests the frame it lands in, and
 * `REGION_FEATURE_CAP` is a client memory and render bound rather than a
 * history-quota one.
 */
export default function AtlasLayout({
  world_layer,
  detail_layer,
  my_tastings,
  my_wishlist,
  winery_layer,
  winery_facets,
  place,
  grape,
  winery,
  search,
  atlas,
  map_zoom,
  auth,
  children,
}: AtlasLayoutProps) {
  const [status, setStatus] = useState<MapStatus>('loading')
  const [zoom, setZoom] = useState(INITIAL_ZOOM)

  // The atlas is public; a journal is what an account is for. Signed out, the
  // chrome offers Sources and a way in where a signed-in reader finds their
  // Journal and Wishlist, and the layer list leaves out the two layers that
  // could only ever be empty.
  const signedIn = auth?.signed_in ?? true

  // The name under the pointer is NOT state here. It lives in `HoverLabel`,
  // reached through this ref, so the map's hover callback is stable and a name
  // change re-renders one tooltip rather than the whole shell.
  const hoverLabel = useRef<HoverLabelHandle>(null)
  const onHover = useCallback(
    (name: string | null, about?: HoverAbout) => hoverLabel.current?.show(name, about),
    [],
  )

  // THE COUNTRIES THE MAP IS HANDED: the first `world_layer` to arrive, and
  // that one for good.
  //
  // The prop is `once` (map_props.rb): the server sends the countries one time
  // and the client carries them from page to page, so an object that turns up
  // under that name later is never new countries. It is a COPY of these. A
  // rating saved from a winery's panel is written into the page with
  // `router.replaceProp`, and Inertia changes one prop by deep-copying every
  // prop the page has. Handed on, each copy would have the map index and tile
  // all 237 countries again, 286 KB of them, for every wine he rates. Only the
  // map is held to the first one; the list and the ribbon read the prop, and a
  // new array to them is a comparison.
  const worldLayer = useRef<AtlasLayer | null>(null)
  worldLayer.current ??= world_layer ?? null

  const [regionLayer, setRegionLayer] = useState<DetailLayer | null>(detail_layer ?? null)
  // The journal layers default ON: they are the reader's own drinking, which is
  // the point of the app, and a layer you have to remember to turn on is a layer
  // you forget exists.
  const [visibility, setVisibility] = useState({
    countries: true,
    regions: true,
    wineries: true,
    tastings: true,
    wishlist: true,
  })

  // Every feature any frame has delivered, keyed by slug and ordered
  // least-recently-seen first. A Map rather than an array because merging is a
  // per-slug question and eviction is a per-slug answer.
  const held = useRef<Map<string, DetailFeature> | null>(null)
  held.current ??= featureMap(detail_layer)

  // Which ground those features were answered for, so a camera that comes to
  // rest inside it asks for nothing (lib/region_frames.ts).
  const memory = useRef<RegionMemory | null>(null)
  memory.current ??= new RegionMemory()

  // Where the camera is now, what is being asked for, and which frame's failure
  // the reader has already waved away. Refs rather than state: nothing here
  // renders, and a camera settle must not re-render the chrome.
  const frame = useRef<MapViewport | null>(null)
  const asked = useRef<RegionRequest | null>(null)
  const dismissedFrame = useRef<string | null>(null)
  const fetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // The winery layer, held here for the same reason the region layer is: both
  // are `optional` props, so neither is re-sent on the next visit, and a click
  // through to a producer would otherwise blank the layer it was clicked on.
  const [wineryLayer, setWineryLayer] = useState<WineryLayer | null>(winery_layer ?? null)
  const [viewport, setViewport] = useState<MapViewport | null>(null)
  const [filters, setFilters] = useState<WineryFilterState>(EMPTY_WINERY_FILTERS)

  // Which of the header's two popovers is open, if either. One value, because
  // Layers and Filters are mutually exclusive: opening one closes the other.
  // There is no outside-click dismissal on purpose — the filters are a working
  // palette used WHILE looking at the map, and a palette that shut on every pan
  // would have to be reopened for every second thought.
  const [popover, setPopover] = useState<ChromePopover | null>(null)

  // The request the layer currently holds or has in flight, as a key. It is what
  // an arriving response is compared against, and what stops the same viewport
  // being asked for twice.
  const wineryRequested = useRef<string | null>(winery_layer ? echoKey(winery_layer) : null)
  // The one request that has been asked for a SECOND time because its first
  // answer never reached the layer (see the effect below). Once, so a key the
  // server will not echo back cannot turn into a request per render.
  const wineryReasked = useRef<string | null>(null)

  const [regionFetch, setRegionFetch] = useState<RegionFetch>(detail_layer ? 'ready' : 'idle')
  const wineryFetch = useLayerFetch('winery_layer', winery_layer ? 'ready' : 'idle')

  // WHICH PANEL THE PAGE ITSELF IS, as one key — or null for the bare map. It
  // is the identity everything below compares against: whether a click is for
  // what is already open, whether the page a reader chose has landed, and
  // whether a navigation has happened at all.
  const pageKey = place
    ? panelKey({ type: 'place', slug: place.slug })
    : winery
      ? panelKey({ type: 'winery', slug: winery.slug })
      : grape
        ? panelKey({ type: 'grape', slug: grape.slug })
        : null

  // The same identity as a path, for the one thing that needs to come back to
  // this page: the way in.
  const pagePath = place
    ? panelPath({ type: 'place', slug: place.slug })
    : winery
      ? panelPath({ type: 'winery', slug: winery.slug })
      : grape
        ? panelPath({ type: 'grape', slug: grape.slug })
        : null

  // THE PANEL A READER HAS ASKED FOR AND NOT YET BEEN GIVEN.
  //
  // Opening and closing a panel are navigations — the panel is URL-driven, so
  // a click and a deep link produce the same state — and a navigation is a
  // round trip. Nothing on screen used to change until it came back. Now the
  // shell shows where the reader is GOING at once and lets the page catch up:
  //
  // - `open`: a stand-in panel with the name the client already holds, the
  //   selection drawn on the map, the header moved aside. When the page lands
  //   the stand-in is replaced by the page's own panel; if it does not land,
  //   the stand-in says so and offers Retry and Close.
  // - `close`: the panel is simply gone, while the visit home is still in the
  //   air.
  //
  // This is state in the shell and nothing else. It is NOT an Inertia "instant
  // visit" (`router.visit(url, { component })`), which looks like the same
  // idea: that swaps in an intermediate page built without the `once` props, so
  // the follow-up request cannot say it already holds the country layer and the
  // server sends all 300 KB of it again on every open and every close.
  const [pending, setPending] = useState<PendingPanelState | null>(null)
  const pendingRef = useRef(pending)
  pendingRef.current = pending

  // What the dock draws, in the order the questions are asked. A stand-in for
  // a page that has since landed is not drawn — the page's own panel is — even
  // in the one render before the effect below clears the state, which is the
  // render the camera measures the dock in.
  const awaited = pending?.kind === 'open' && panelKey(pending.target) !== pageKey ? pending : null
  const closing = pending?.kind === 'close'

  // Whether a panel is on screen, for everything that makes room for one: the
  // header's reservation, the notices, the attribution, the camera's insets.
  // It follows what the reader was just SHOWN, so the chrome moves once, with
  // the click, rather than a second time when the page lands.
  const panelOpen = awaited ? true : closing ? false : pageKey !== null

  // Navigations only. Inertia fires `start` and `finish` for EVERY request, the
  // async partial reloads included, so an unfiltered listener announced
  // "Loading…" on every pan settle and every debounced keystroke — a status
  // that fires that often is noise, and the layers and the search box each say
  // their own. The optional chaining is for the event itself: a listener can be
  // called with no event at all, and that must read as a navigation.
  const [navigating, setNavigating] = useState(false)
  useEffect(() => {
    const started = router.on('start', (event) => {
      if (!event?.detail?.visit?.async) setNavigating(true)
    })
    const finished = router.on('finish', (event) => {
      if (!event?.detail?.visit?.async) setNavigating(false)
    })

    return () => {
      started()
      finished()
    }
  }, [])

  // Everything a handler below needs that changes from render to render, read
  // through one ref. The handlers are handed to the map and to the search box,
  // and a handler that changed identity whenever a prop did would re-render
  // both on every pan; this keeps each of them the same function for the life
  // of the shell while still answering for the page as it is now.
  const latest = useRef({ pageKey, panelOpen, world_layer, search, wineryLayer, fine: map_zoom.fine, detail: map_zoom.detail })
  latest.current = { pageKey, panelOpen, world_layer, search, wineryLayer, fine: map_zoom.fine, detail: map_zoom.detail }

  // Timers outlive a render but must not outlive the shell.
  const intentTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (fetchTimer.current) clearTimeout(fetchTimer.current)
      if (pendingTimer.current) clearTimeout(pendingTimer.current)
      if (intentTimer.current) clearTimeout(intentTimer.current)
    },
    [],
  )

  // A prefetched page carries the shared props as they were when it was
  // fetched, the reader's own pins among them. The shell mounting is the reader
  // ARRIVING at the map — from the journal, from a tasting they just saved —
  // so anything fetched before they left is dropped rather than trusted
  // (`PANEL_PREFETCH_CACHE_MS` has the rest of the reasoning).
  useEffect(() => {
    router.flushAll?.()
  }, [])

  // The three panel pages' code, asked for once the map has drawn and the
  // browser has nothing better to do (`PANEL_PAGES`). Not before: the map's own
  // chunk and first tiles are what the reader is waiting on until then.
  useEffect(() => {
    if (status !== 'ready') return

    const warm = () => warmPages(PANEL_PAGES)

    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(warm, { timeout: PANEL_WARM_WAIT_MS })

      return () => window.cancelIdleCallback?.(handle)
    }

    const timer = setTimeout(warm, PANEL_WARM_WAIT_MS)

    return () => clearTimeout(timer)
  }, [status])

  // One partial reload for one frame of ground. No endpoint and no route: an
  // Inertia partial reload targets the page the reader is already on, which is
  // how this works identically on `/`, `/places/:slug` and `/grapes/:slug`.
  //
  // WHAT IS ASKED FOR IS NOT WHAT IS ON SCREEN. It is the screen and a margin
  // round it (`RegionMemory#frameToAsk`), and the answer is remembered as
  // ground the shell now holds, so the next settle inside it asks for nothing.
  //
  // AN ANSWER IS ALWAYS MERGED, even when the camera has since moved on. This
  // used to drop a response for a frame the camera had left — the rule the
  // search box applies to a query it has typed past — and for a layer that
  // REPLACES what is drawn that is right. This one merges: the appellations in
  // the answer are really there, drawing them is never wrong, and throwing
  // them away meant a reader who panned off and back paid for the same ground
  // twice. (It is also what lets a frame be asked for before the camera has
  // arrived in it.)
  const loadRegions = useCallback((viewport: MapViewport) => {
    const ground = memory.current!.frameToAsk(viewport)
    const request: RegionRequest = {
      frame: frameOf(ground, latest.current.fine),
      viewKey: viewportKey(viewport),
      padded: ground !== viewport,
      from: currentPath(),
      answered: false,
    }
    asked.current = request

    // Only the request the shell is still waiting on may give the ground up.
    // One that a newer request has superseded has nothing to release.
    const release = () => {
      if (asked.current === request) asked.current = null
    }

    const failed = () => {
      request.answered = true

      // A superseded request says nothing: the newer one owns the banner, and
      // a frame the camera has already been re-asked for must not raise a
      // failure over a map that is drawing fine from the answer to that.
      if (asked.current !== request) return

      // Re-askable: the ground was never delivered, so the next settle on it
      // is a real request again rather than a no-op against one in flight.
      asked.current = null

      // And nothing is said if the camera has left the ground this would have
      // covered, or pulled back to where no appellation is drawn at all.
      const now = frame.current
      if (now && now.zoom < latest.current.detail) return
      if (now && !frameCovers(request.frame, frameOf(now, latest.current.fine))) return

      setRegionFetch(dismissedFrame.current === request.viewKey ? 'dismissed' : 'failed')
    }

    router.reload({
      only: ['detail_layer'],
      data: { bbox: viewportBbox(ground), zoom: viewportZoom(viewport) },
      // A camera settle is not a navigation. `preserveUrl` keeps `?bbox=` out
      // of the address bar — a frame is not a deep link, and a URL carrying
      // one would stop working the moment it was reloaded, because the prop is
      // `optional` and a plain GET never computes it. `replace` is what keeps
      // the entry out of history, so Back leaves the map instead of replaying
      // every pan the reader made to get here.
      preserveUrl: true,
      replace: true,
      // Delayed, so a settle that resolves quickly announces nothing and only
      // a genuinely slow fetch puts a badge over the map. Once per camera
      // settle, a badge that appears on every pan is noise rather than news.
      onStart: () => {
        // Cleared first: two reloads can overlap when a settle supersedes one
        // already in flight, and the earlier timer would otherwise be dropped
        // unreferenced and strand its badge over the map.
        if (pendingTimer.current) clearTimeout(pendingTimer.current)
        pendingTimer.current = setTimeout(() => setRegionFetch('pending'), REGION_PENDING_DELAY_MS)
      },
      onSuccess: (page) => {
        request.answered = true

        const layer = (page.props as { detail_layer?: DetailLayer }).detail_layer

        // INERTIA CAN CALL THIS WITH SOMEBODY ELSE'S PAGE. An async response
        // that lands after a navigation is not applied — the page changed
        // under it — but its `onSuccess` still runs, handed the page the
        // reader is on NOW. That page's `detail_layer`, if it has one, answers
        // a different request; remembering this request's ground on the
        // strength of it would mark as held ground that was never delivered,
        // and the map would never ask for it again. So the ground is given up
        // instead, and the settle check below asks for it from the new page.
        if (!layer || wentElsewhere(page, request.from)) {
          release()
          if (frame.current) checkRegions.current(frame.current)
          return
        }

        setRegionFetch('ready')

        // Merged here rather than in an effect on the `detail_layer` prop,
        // because this is where the frame that was ASKED for is still in
        // scope; the prop cannot say which request produced it.
        const { changed, evicted } = mergeRegionFeatures(held.current!, layer, REGION_FEATURE_CAP)

        // A frame the cap cut short is drawn and not remembered (see
        // `RegionMemory`), and if it was a padded one the margin is what cost
        // it: that depth is asked for bare from now on, starting again with
        // the frame the camera is in.
        if (layer.truncated) {
          if (request.padded) memory.current!.crowd(request.frame.level)
        } else {
          memory.current!.remember(request.frame)
        }
        if (evicted) memory.current!.forgetAllBut(layer.truncated ? null : request.frame)

        // Nothing new, nothing redrawn: the map is handed a new collection
        // only when the set it would draw from has actually changed.
        if (changed) setRegionLayer({ type: 'FeatureCollection', features: [...held.current!.values()] })

        release()
        if (layer.truncated && request.padded && frame.current) checkRegions.current(frame.current)
      },
      onError: failed,
      // The public atlas refuses too many requests with a plain-text 429,
      // which is not an Inertia response: it reaches neither callback above
      // and would open Inertia's error dialog over the map. It is this
      // frame failing, so it is settled exactly as `onError` settles one —
      // the banner and its Retry — and the dialog is declined. Every other
      // foreign response keeps the dialog (see lib/rate_limit.ts).
      onHttpException: (response) => {
        if (!isRateLimited(response)) return

        failed()
        return false
      },
      // A cancelled request fires neither onSuccess nor onError, and a cancel
      // is an ORDINARY event: Inertia cancels every reload in flight when the
      // reader navigates to another page. P1 reported that as a failure, which
      // would raise the banner on a click. It goes back to idle instead — and
      // the ground is given up, because a request that will never be answered
      // must not go on looking like one in flight. The check that runs when
      // the navigation lands asks for it again.
      onFinish: () => {
        if (pendingTimer.current) clearTimeout(pendingTimer.current)
        setRegionFetch((state) => (state === 'pending' ? 'idle' : state))
        if (!request.answered) release()
      },
    })
  }, [])

  // Whether the camera's frame needs asking for, and if so, when.
  //
  // It needs nothing below the threshold (no appellation is drawn there, and
  // every one's own `min_zoom` starts at it), nothing inside ground the shell
  // already holds, and nothing inside ground a request in flight is about to
  // deliver. Otherwise one request, a moment later — see
  // `REGION_FETCH_DEBOUNCE_MS` for what the moment is for.
  //
  // In every case a request already scheduled is cancelled first: one queued
  // at zoom 7 would otherwise still fire after the reader has zoomed back out
  // to the world, spending a round trip on a frame nobody is looking at.
  //
  // Reached through a ref because the request's own callbacks call it, and it
  // schedules the request.
  const checkRegions = useRef<(viewport: MapViewport, immediately?: boolean) => void>(() => {})
  checkRegions.current = (viewport: MapViewport, immediately = false) => {
    if (fetchTimer.current) clearTimeout(fetchTimer.current)
    fetchTimer.current = null

    if (viewport.zoom < map_zoom.detail) return

    const view = frameOf(viewport, map_zoom.fine)
    if (memory.current!.covers(view)) return
    if (asked.current && frameCovers(asked.current.frame, view)) return

    if (immediately) loadRegions(viewport)
    else fetchTimer.current = setTimeout(() => loadRegions(viewport), REGION_FETCH_DEBOUNCE_MS)
  }

  // Once per camera settle. `moveend` fires once per gesture, so this is one
  // report per gesture — and, inside ground the shell holds, no request at all.
  const onViewport = useCallback((viewport: MapViewport) => {
    frame.current = viewport
    // The winery layer is drawn at every zoom, so it takes the frame before
    // the appellation layer's checks can return.
    setViewport(viewport)
    checkRegions.current(viewport)
  }, [])

  // Where the camera is GOING, said as it leaves. The frame it will land in is
  // asked for now, with no wait — nothing else will report during a flight to
  // be folded in with it — so the appellations there arrive while the camera
  // is in the air. When it lands, the settled frame is inside ground already
  // held or already on its way, and asks for nothing.
  //
  // Only the appellations. The winery layer answers exactly one frame and
  // echoes it back to five decimals, and a guess will not match that.
  const onDestination = useCallback((viewport: MapViewport) => {
    checkRegions.current(viewport, true)
  }, [])

  // The frame the camera is in NOW, not the one that failed: the reader has kept
  // moving while the banner was up, and redrawing where they no longer are is
  // not what Retry offers.
  const retryRegions = useCallback(() => {
    if (frame.current) loadRegions(frame.current)
  }, [loadRegions])

  const dismissRegionFailure = useCallback(() => {
    dismissedFrame.current = frame.current ? viewportKey(frame.current) : null
    setRegionFetch('dismissed')
  }, [])

  // A toggle for a layer the atlas holds nothing for would be enabled-but-inert,
  // which reads as broken. `winery_facets` counts the whole live atlas, so this
  // is the honest answer on first paint rather than after a round trip.
  const wineriesEnabled = winery_facets.total > 0
  const wineriesVisible = visibility.wineries && wineriesEnabled

  const wineryRequest = useMemo(
    () => (viewport ? canonicalRequest(wineryViewportOf(viewport), filters) : null),
    [viewport, filters],
  )

  // The first fetch is an effect over held state — mounted, visible, viewport
  // known, nothing held for it yet — not a handler hung off a map event.
  // `winery_layer` is `optional`, so a plain GET never computes it, and MapLibre
  // sets its initial camera inside the constructor before any listener exists:
  // a move-only trigger means a reader who loads the map and does not touch it
  // sees the toggle on and no layer, with nothing on screen to explain it. The
  // map event's only job is to put the viewport into this state.
  //
  // Asked for exactly the frame on screen, unlike the appellations. "Showing N
  // of M wineries here" is a statement about what the reader can see, and a
  // margin round the request would make it count ground they cannot.
  const loadWineryFetch = wineryFetch.load
  useEffect(() => {
    if (!wineriesVisible) {
      // Turning the layer off forgets what was asked for, so turning it back on
      // is a fresh request rather than a no-op against a key already held.
      wineryRequested.current = null
      return
    }

    if (!wineryRequest) return

    const key = requestKey(wineryRequest)
    if (wineryRequested.current === key) return

    wineryRequested.current = key
    loadWineryFetch(requestData(wineryRequest))
  }, [wineriesVisible, wineryRequest, loadWineryFetch])

  // A response the map has already outrun is dropped, not drawn. A pan races far
  // harder than a keystroke and the failure is worse than a stale dropdown:
  // Bordeaux's clusters over a viewport now showing the Mosel, with counts that
  // are confidently wrong for what is on screen.
  //
  // And an answer that says what the layer already holds is not drawn again.
  // The prop arrives as a new object without being a new answer: a rating
  // saved from a winery's panel copies every prop the page has (`worldLayer`
  // above has the mechanism), and this one is in the page from the first pan
  // on. Handed to the map, each copy would be the dots on screen sent to the
  // worker again for every wine he rates. Two answers for ONE frame are
  // compared as text, which a copy matches exactly; an answer for new ground
  // has a new key and is never read through.
  useEffect(() => {
    if (!winery_layer) return
    if (echoKey(winery_layer) !== wineryRequested.current) return

    setWineryLayer((held) =>
      held && echoKey(held) === echoKey(winery_layer) && JSON.stringify(held) === JSON.stringify(winery_layer)
        ? held
        : winery_layer,
    )
  }, [winery_layer])

  // THE REQUEST THAT WAS ASKED FOR AND NEVER ARRIVED.
  //
  // `wineryRequested` says a frame has been asked for; it cannot say the answer
  // came. Two ordinary things lose one. A navigation cancels every reload in
  // flight — a reader who pans and then clicks a dot before the dots have been
  // answered — and Inertia does not apply an async response that lands after
  // the page has changed under it, though it still calls it a success. Either
  // way the layer goes on drawing the frame before, the key says the new frame
  // is in hand, and nothing asks again until the camera moves.
  //
  // So at the two moments one can have been lost — the page has just changed,
  // or a fetch has just been called a success — the frame on screen is checked
  // against what the layer actually holds, and asked for again if they differ.
  // Once per frame, so a key the server will not echo back is one extra
  // request rather than a loop. A fetch that FAILED is left alone: it has a
  // banner and a Retry, and retrying behind the reader's back would take both
  // away.
  //
  // On those two TRANSITIONS, not whenever the state happens to read idle: a
  // request that has only just been issued is also "not pending yet and not
  // held yet", and asking for it a second time would double every pan. And a
  // CANCELLED fetch waits for the page to change rather than being asked again
  // on the spot: the navigation that cancelled it is still in the air, and a
  // request made now would be made from the page being left and thrown away
  // in its turn.
  const wineryState = wineryFetch.state
  const wineryNow = useRef({ wineriesVisible, wineryRequest, wineryState, winery_layer, wineryLayer })
  wineryNow.current = { wineriesVisible, wineryRequest, wineryState, winery_layer, wineryLayer }

  const reaskWineries = useCallback(() => {
    const now = wineryNow.current
    if (!now.wineriesVisible || !now.wineryRequest) return
    if (now.wineryState === 'pending' || now.wineryState === 'failed' || now.wineryState === 'dismissed') return

    const key = requestKey(now.wineryRequest)
    if (wineryRequested.current !== key || wineryReasked.current === key) return
    // Either is the answer: the prop as it arrives, or the copy held from it.
    if (now.winery_layer && echoKey(now.winery_layer) === key) return
    if (now.wineryLayer && echoKey(now.wineryLayer) === key) return

    wineryReasked.current = key
    loadWineryFetch(requestData(now.wineryRequest))
  }, [loadWineryFetch])

  const wineryBefore = useRef({ pageKey, state: wineryState })
  useEffect(() => {
    const before = wineryBefore.current
    wineryBefore.current = { pageKey, state: wineryState }

    if (before.pageKey !== pageKey) {
      reaskWineries()
      return
    }

    if (before.state !== 'pending' || wineryState !== 'ready') return

    // "Success" and the layer it brought come out of one Inertia update but
    // not always out of one render: the state can read `ready` a frame before
    // the prop is there. Looked at straight away, an answer that is merely on
    // its way in reads as one that never came, and every slow pan would be
    // asked for twice. So the look is taken a moment later.
    const timer = setTimeout(reaskWineries, WINERY_ANSWER_GRACE_MS)

    return () => clearTimeout(timer)
  }, [pageKey, wineryState, reaskWineries])

  // The same recovery for the appellations, when a navigation lands: a request
  // made from the page the reader just left will be thrown away when it
  // arrives, so it is given up now, and the frame on screen is checked again
  // from the page they are on.
  useEffect(() => {
    if (asked.current && asked.current.from !== currentPath()) asked.current = null
    if (frame.current) checkRegions.current(frame.current)
  }, [pageKey])

  // AND THE SAME RECOVERY FOR BOTH LAYERS, WHEN A RATING HAS BEEN SENT.
  //
  // A winery's panel rates the reader's wines where they are (winery_my_wines).
  // A rating is a visit to another URL, so starting one cancels every layer
  // reload in the air, exactly as a navigation does — and the two recoveries
  // above both wait for "the page has changed", which a rating never brings:
  // the reader is still on the winery's page. Pan, rate at once, and the dots
  // for the new frame would stay undrawn until the camera next moved.
  //
  // So the moment is the rating's own finish: an ASYNC visit that is NOT A
  // GET. Every reload this shell and the search box make is an async GET, and
  // a navigation is not async, so nothing else answers to that description.
  // The once-per-frame guard is cleared first, because it is for a
  // navigation's one lost answer, and he rates a wine a second: the next
  // rating out of the queue may cancel this re-ask in its turn, and its own
  // finish has to be free to ask again. The queue is empty a moment later.
  //
  // Not for a rating that was itself CANCELLED. The outbox sends that one
  // again, and whatever cancelled it is still in the air: a request made now
  // would be thrown away with the page, and would spend the guard doing it.
  //
  // AND THE COUNTRIES, WHEN THEY NEVER LANDED. They are deferred: Inertia
  // fetches them in a request of its own straight after the page, and a wine
  // rated before that has answered cancels it like the other two. Nothing
  // asks again by itself: the rating's answer is a partial one, and the server
  // names what is still to fetch only on a full page. So until he left the
  // page the map had no countries, the list said "Loading the countries…",
  // and no panel was fetched ahead of a click. This is the reload Inertia's
  // own deferred fetch makes. Quiet when it is refused or dropped, like every
  // layer he did not ask for by name.
  useEffect(
    () =>
      router.on('finish', (event) => {
        const visit = event?.detail?.visit
        if (!visit?.async || visit.method === 'get' || visit.cancelled) return

        wineryReasked.current = null
        reaskWineries()
        asked.current = null
        if (frame.current) checkRegions.current(frame.current)

        if (!latest.current.world_layer) {
          router.reload({ only: ['world_layer'], onHttpException: declineRateLimitDialog, onNetworkError: () => false })
        }
      }),
    [reaskWineries],
  )

  // What the chrome is covering, for the camera: the header always, and the
  // panel when one is open — to the right from `md`, from the bottom below it.
  // A function, called by the map at the moment it moves, so it answers for the
  // window as it is then (see `chromeInsets`).
  //
  // On a phone the bottom is the dock's own height, measured: the dock IS
  // everything anchored to that edge, the sheet included, and the sheet is as
  // tall as what it says. By the time the map asks, the panel the camera is
  // moving for has been committed — it is rendered in the same pass that
  // changes the camera — so this measures the sheet being opened, not the one
  // before it. From `md` the dock is `display: contents` and measures nothing.
  const dock = useRef<HTMLDivElement>(null)
  //
  // Unless the reader has pulled the sheet up TALL (`DetailPanel`). That is a
  // reader choosing the panel over the map, and a camera that then fitted a
  // place into the 100px left above it would be answering a question nobody
  // asked; the bottom is taken at its ordinary most, as when there is nothing
  // to measure.
  const cameraInsets = useCallback(
    () =>
      chromeInsets(
        panelOpen,
        dock.current?.querySelector(':scope > aside[data-tall]') ? undefined : dock.current?.getBoundingClientRect().height,
      ),
    [panelOpen],
  )

  // WHERE THE READER IS (lib/locate.ts): the button above the attribution, the
  // dot it puts on the map, and the one unprompted ask on a first opening of
  // the bare home view.
  //
  // The shell's whole part in it is to lend `Locate` five things, none of
  // which changes identity from render to render — so the shell's renders stop
  // at `Locate`, which is memoised — and each of the two functions answers for
  // the page as it is when asked and not as it was when it was handed over:
  //
  // - `mapHandle`: where the hook leaves the map and its mover.
  // - `locateInsets`: what the chrome is covering now.
  // - `plainNow`: whether this is still the bare map — no panel open and none
  //   on its way.
  // - `openedBare`: whether the shell was MOUNTED on the bare map. A page
  //   opened on a place, a producer or a grape was sent to show that thing: it
  //   never asks unprompted and keeps its camera, and closing its panel does
  //   not turn it into a first opening.
  // - `noticeStack`: the notice stack's element, which a failed press adds a
  //   line to from wherever the control is rendered.
  //
  // NO POSITION EVER REACHES THIS COMPONENT. Not as state, not as a prop of
  // anything it renders.
  const mapHandle = useRef<MapReach | null>(null)
  const noticeStack = useRef<HTMLDivElement>(null)
  const openedBare = useRef(pageKey === null).current
  const insetsNow = useRef(cameraInsets)
  insetsNow.current = cameraInsets
  const locateInsets = useCallback(() => insetsNow.current(), [])
  const plainNow = useCallback(() => latest.current.pageKey === null && pendingRef.current === null, [])

  // The one camera the map is given: the open place's, or the open producer's.
  // A grape has no geometry and moves nothing.
  //
  // From the PAGE, never from a panel that is only on its way. The camera
  // moves when its key changes, and a place's camera is a box the server works
  // out from the finer of its two shapes — the shell holds the coarser one, and
  // a second implementation of that box here would be two cameras for one
  // place. So the map moves when the page lands, which for a page the reader
  // hovered first is the same moment as the click.
  const camera = place ? cameraFor(place) : winery ? wineryCamera(winery, map_zoom.winery_points) : null

  // The two cannot share a phone screen: the detail panel is a sheet taking up
  // to 60dvh from the bottom, and below `md` Layers and Filters are sheets from
  // the same edge, drawn above it. A panel that opened underneath one would be
  // a click that appeared to do nothing. From `md` up they are popovers on the
  // left and the panel is on the right, so they stay where the reader left them.
  useEffect(() => {
    if (!panelOpen) return
    if (!window.matchMedia?.(PHONE_MEDIA_QUERY).matches) return

    setPopover(null)
  }, [panelOpen])

  // The stand-in's job is over the moment the page it stood for is the page.
  useEffect(() => {
    if (pending?.kind === 'open' && panelKey(pending.target) === pageKey) setPending(null)
  }, [pending, pageKey])

  // Which visit the pending state belongs to. A reader who clicks a second
  // place before the first has answered starts a second visit, and Inertia
  // cancels the first — whose callbacks then arrive late and must not clear a
  // stand-in that is no longer theirs.
  const visitGeneration = useRef(0)
  const visitToken = useRef<{ cancel: () => void } | null>(null)

  // When the panel's frame last appeared from nothing (`PanelEnteredAtContext`).
  const shellSince = useRef<number | null>(null)

  // What a failed panel visit does, as two functions that are THE SAME
  // FUNCTIONS for the life of the shell — and that identity is load-bearing.
  //
  // Inertia decides whether a visit can be served from a prefetch by comparing
  // the two requests' parameters, and of all a visit's callbacks these two are
  // among the ones it compares. Handed a fresh closure each time, a visit would
  // never match the prefetch made for it: the hover would cost a request and
  // the click would cost another. So the prefetch and the visit are both given
  // exactly these, and what they do is look at whichever panel is pending.
  //
  // Neither swallows the failure. A page that answers with something that is
  // not an Inertia response keeps Inertia's dialog, which in development is the
  // Rails error page; only a rate-limit refusal is declined, because the
  // stand-in already says "couldn't load" with a Retry, and that is the right
  // size for it (lib/rate_limit.ts). And a network error has to be left to
  // Inertia: its prefetch bookkeeping only lets go of a request it sees fail,
  // and one swallowed here would leave a later click on the same place waiting
  // on a prefetch that is never coming.
  const panelVisit = useRef({
    onHttpException: (response: { status: number }) => {
      setPending((current) => (current?.kind === 'open' ? { ...current, failed: true } : current))

      return declineRateLimitDialog(response)
    },
    onNetworkError: () => {
      setPending((current) => (current?.kind === 'open' ? { ...current, failed: true } : current))
    },
  }).current

  const clearIntent = useCallback(() => {
    if (intentTimer.current) clearTimeout(intentTimer.current)
    intentTimer.current = null
  }, [])

  // THE ONE WAY A PANEL IS OPENED, whatever was clicked: a polygon, a dot, a
  // search result, a breadcrumb. It is what knows whether a panel is already
  // open, and therefore whether the visit replaces the history entry — on a
  // map, Back means "close this" whichever panel is showing.
  const openPanel = useCallback(
    (raw: PanelTarget) => {
      const now = latest.current
      const target = describe(raw, held.current!, now.world_layer, now.search, now.wineryLayer)

      // Chosen again while its page is still on its way — the second click of
      // a double click, most often. The visit is already in the air and a
      // second one would only race it. (A stand-in that FAILED is different:
      // choosing it again is asking again.)
      const waiting = pendingRef.current
      if (waiting?.kind === 'open' && !waiting.failed && panelKey(waiting.target) === panelKey(target)) return

      const generation = (visitGeneration.current += 1)

      // The page about to be visited must not also be prefetched a moment
      // later by a pointer that was resting on it.
      clearIntent()
      visitToken.current = null

      // Chosen again while it is already open: a visit and nothing more. A
      // stand-in here would blank a panel that has its content.
      if (panelKey(target) === now.pageKey) {
        setPending(null)
      } else {
        if (!now.panelOpen) shellSince.current = performance.now()

        // The sheet that is up now, measured before it is replaced: the
        // stand-in is held at its height on a phone. One read of one box, on
        // a click.
        const sheet = now.panelOpen ? dock.current?.querySelector(':scope > aside') : null
        const held = sheet ? sheet.getBoundingClientRect().height : null

        setPending({ kind: 'open', target, failed: false, held: held && held > 0 ? held : null })
      }

      // Either way the visit is over when it succeeds or is cancelled, and
      // only the newest visit may say so. `onSuccess` is what ends a stand-in
      // whose page landed somewhere other than where it was aimed — a merged
      // producer redirects to the record it was merged into.
      const settle = () => {
        if (visitGeneration.current === generation) setPending(null)
      }

      router.visit(panelPath(target), {
        // Replaced when the page being left is itself a panel.
        replace: now.pageKey !== null,
        ...panelVisit,
        onCancelToken: (token) => {
          if (visitGeneration.current === generation) visitToken.current = token
        },
        onSuccess: settle,
        onCancel: settle,
      })
    },
    [clearIntent, panelVisit],
  )

  // The three shapes the map and the search box report a choice in. A grape
  // moves no camera: it has no geometry, and there is no `place` prop on its
  // page. A producer has its own URL namespace, because a producer's name and a
  // place's name genuinely collide in wine (KTD15).
  const selectPlace = useCallback((slug: string) => openPanel({ type: 'place', slug }), [openPanel])
  const selectGrape = useCallback((slug: string) => openPanel({ type: 'grape', slug }), [openPanel])
  const selectWinery = useCallback((slug: string) => openPanel({ type: 'winery', slug }), [openPanel])

  // Close, from the panel's own control or Escape. The panel is gone at once;
  // the visit home follows. A stand-in over the bare map has no page to leave,
  // so closing it is only taking it away.
  const closePanel = useCallback(() => {
    const generation = (visitGeneration.current += 1)
    const standInOnly = pendingRef.current?.kind === 'open' && latest.current.pageKey === null

    clearIntent()
    visitToken.current?.cancel()
    visitToken.current = null

    if (standInOnly) {
      setPending(null)
      return
    }

    setPending({ kind: 'close' })
    router.visit(MAP_ROOT_PATH, {
      // Fires when the visit ends however it ends. If it failed, the page is
      // still the place and its panel comes back, which is the truth.
      onFinish: () => {
        if (visitGeneration.current === generation) setPending(null)
      },
    })
  }, [clearIntent])

  const retryPanel = useCallback(() => {
    if (pendingRef.current?.kind === 'open') openPanel(pendingRef.current.target)
  }, [openPanel])

  // "Close" on a stand-in that failed: put back whatever was there before it —
  // the panel of the page the reader is still on, or the bare map.
  const dismissPanel = useCallback(() => {
    visitGeneration.current += 1
    visitToken.current?.cancel()
    visitToken.current = null
    setPending(null)
  }, [])

  // A PAGE IS FETCHED WHEN A READER IS ABOUT TO ASK FOR IT (see
  // `PANEL_PREFETCH_DWELL_MS`). One timer for every source of intent, so a
  // pointer that moves from a polygon to a breadcrumb is one intention and not
  // two. `router.prefetch` is called optionally because a test's stand-in for
  // the router does not define it.
  const intend = useCallback(
    (target: PanelTarget | null) => {
      clearIntent()
      if (!target || panelKey(target) === latest.current.pageKey) return

      // Nor the page a visit is already fetching: the pointer is usually still
      // resting on the thing that was just clicked.
      const waiting = pendingRef.current
      if (waiting?.kind === 'open' && panelKey(waiting.target) === panelKey(target)) return

      intentTimer.current = setTimeout(() => {
        intentTimer.current = null

        // Not before the country layer has landed. It is a `once` prop, and a
        // page fetched without being able to say "I have it" comes back
        // promising to fetch it again.
        if (!latest.current.world_layer) return

        router.prefetch?.(panelPath(target), panelVisit, { cacheFor: PANEL_PREFETCH_CACHE_MS })
      }, PANEL_PREFETCH_DWELL_MS)
    },
    [clearIntent, panelVisit],
  )

  // From the pointer, and so only where there is one. A touch screen sends the
  // map a compatibility `mousemove` with every tap, which is not a reader
  // resting on anything.
  const intendByHover = useCallback(
    (target: PanelTarget | null) => {
      if (target && !window.matchMedia?.('(hover: hover)').matches) return

      intend(target)
    },
    [intend],
  )

  const panelNavigation = useMemo<PanelNavigation>(
    () => ({ open: openPanel, intend: intendByHover }),
    [openPanel, intendByHover],
  )

  // Whether the panel about to be drawn is ARRIVING or only changing what it
  // describes: there was no panel in the last render that was committed. A ref
  // written after each commit rather than state, because the answer is needed
  // by the render that mounts the panel and must not cause another.
  const hadPanel = useRef(false)
  const entering = !hadPanel.current
  useEffect(() => {
    hadPanel.current = panelOpen
  })

  // The journal's own pins. A single pin opens its tasting; a CLUSTER opens the
  // journal filtered to that place, because MapLibre separates clustered points
  // by zoom and these points are frequently identical -- every wine that
  // resolves to its country lands on that country's one representative point,
  // so zooming forever never splits them. A cluster that cannot be opened would
  // put those tastings out of reach from the map entirely.
  const selectTasting = useCallback((id: number) => {
    router.visit(tastingPath(id))
  }, [])

  const selectCluster = useCallback((slug: string) => {
    router.get(JOURNAL_PATH, slug ? { region: slug } : {})
  }, [])

  // What is drawn as selected: what the reader was just shown, like the panel.
  //
  // THE PLACE A PAGE SELECTS is the place it is about — or, for a producer, the
  // appellation it sits in. A producer's panel paints that crumb in the
  // selection's ink, and a crumb in the colour of an outline that is not on
  // the map points at nothing; and it is what a producer's page is for on a
  // map: which line this estate is inside. The dot keeps its own ring
  // (`selectedWinerySlug`). A producer the atlas could place in no appellation
  // has no crumb to mark and selects no place.
  //
  // It moves no camera. The camera is the `camera` above, keyed on the place or
  // the producer the page is about, and never reads this.
  const pagePlaceSlug = place?.slug ?? winery?.appellation?.slug ?? null

  // A stand-in for a PLACE selects it at the click. A stand-in for a PRODUCER
  // leaves the place selection where it is until its page lands and says which
  // appellation it is in — the shell knows a dot's name, not its appellation.
  // It used to take the outline off at the click, and that was right while a
  // producer's page selected no place. Now the dot a reader clicks is nearly
  // always inside the place that is open, so taking the outline off for the
  // length of a round trip would be the whole screen's veil blinking off and
  // on around a click that changed nothing about where they are. A stand-in
  // for a grape, and a closing panel, take it off at the click as before.
  const shown = awaited ? awaited.target : null
  const selectedSlug = shown
    ? shown.type === 'place'
      ? shown.slug
      : shown.type === 'winery'
        ? pagePlaceSlug
        : null
    : closing
      ? null
      : pagePlaceSlug
  const selectedWinerySlug = shown
    ? shown.type === 'winery'
      ? shown.slug
      : null
    : closing
      ? null
      : (winery?.slug ?? null)

  // Whether what is selected is a country, as far as the shell can tell: the
  // stand-in's kind when one is up, the page's place otherwise.
  const selectedIsCountry = shown
    ? shown.type === 'place' && shown.kind === COUNTRY_KIND
    : place?.kind === COUNTRY_KIND && place.slug === selectedSlug

  // The open place's own finest shape, which only its page carries. Null for a
  // place still on its way, for the four countries too large to ship one, and
  // for a place with no finer tier — and null means the map draws the selection
  // from the feature it already holds.
  const selectedOutline = place && selectedSlug === place.slug ? place.outline : null

  // The winery pill waits like the region one does (`WINERY_PENDING_DELAY_MS`).
  const wineriesPending = useLate(wineryState === 'pending', WINERY_PENDING_DELAY_MS)

  // Memoised for reference stability, not for the cost of building the object.
  // `useMaplibre`'s visibility effect keys on this value, and a fresh literal
  // every render makes it fire on every render — which is every camera settle
  // (the viewport and both fetch states change) and every popover opened or
  // closed. Each firing re-applies about ten setLayoutProperty/setPaintProperty
  // calls, each of which marks MapLibre's style dirty and schedules a repaint
  // whether or not anything changed. `camera` avoids the same trap by keying
  // its effect on a derived slug; this object has no such primitive to key on,
  // so it is stabilised here.
  const mapVisibility = useMemo(
    () => ({ ...visibility, wineries: wineriesVisible }),
    [visibility, wineriesVisible],
  )

  const regionsEnabled = zoom >= map_zoom.detail
  const atlasIsEmpty = atlas.countries === 0 && atlas.regions === 0
  const mapFailed = status === 'unsupported' || status === 'unavailable'

  // Filters compose as AND, so two individually non-empty ones can intersect to
  // nothing without either control being disabled — `mid` size and `group_owned`
  // independence is a real example at P6a's data volume. A disabled control
  // cannot catch this case, and a blank map with no explanation is exactly the
  // failure KTD11 exists to prevent.
  const noWineriesMatch =
    wineriesVisible && wineryLayer !== null && wineryLayer.total === 0 && activeFilterCount(filters) > 0

  return (
    // The shell is the colour the map draws its own land in, so the canvas
    // arriving over it is not a flash from one grey to another. It is read from
    // the map's constant rather than restated as a class for that reason: the
    // two have to be the same value, and one of them is already the source.
    <div
      className="relative h-dvh w-full overflow-hidden text-ink"
      style={{ backgroundColor: PLAIN_BACKGROUND_COLOR }}
    >
      <Head title="Winemap" />

      {/* Until the map reports that it has drawn: through the chunk request and
          through the style's own load. It sits BEFORE the map in the DOM, so
          the canvas covers it the moment there is something to show, and it is
          unmounted rather than left pulsing underneath. The visible mark is the
          wordmark; the sentence is for a screen reader. */}
      {status === 'loading' && (
        <div className="absolute inset-0 grid place-items-center">
          <p
            aria-hidden="true"
            className="font-display text-2xl font-semibold tracking-tight text-ink/40 motion-safe:animate-pulse"
          >
            Winemap
          </p>
          <p className="sr-only">Loading the map…</p>
        </div>
      )}

      {/* Boundary outside Suspense, not inside it: Suspense handles the lazy
          import while it is pending, and rethrows it when it rejects. Without
          something to catch that, one failed chunk request unmounts this whole
          layout — header, search, toggles and the atlas list with it. The
          fallback draws nothing because the loading mark above already covers
          the pending import, and two of them would be two sentences for a
          screen reader; what it does instead of drawing is in `MapSuspense`. */}
      <MapBoundary onError={() => setStatus('unavailable')}>
        <MapSuspense>
          <AtlasMap
            worldLayer={worldLayer.current}
            countryMinZoom={map_zoom.country}
            regionLayer={regionLayer}
            tastingLayer={my_tastings}
            wishlistLayer={my_wishlist}
            wineryLayer={wineryLayer}
            wineryMarkZoom={map_zoom.winery_points}
            layerVisibility={mapVisibility}
            selectedSlug={selectedSlug}
            selectedWinerySlug={selectedWinerySlug}
            selectedOutline={selectedOutline}
            camera={camera}
            cameraInsets={cameraInsets}
            onStatus={setStatus}
            onZoom={setZoom}
            onViewport={onViewport}
            onHover={onHover}
            onIntent={intendByHover}
            onDestination={onDestination}
            onSelect={selectPlace}
            onSelectTasting={selectTasting}
            onSelectCluster={selectCluster}
            onSelectWinery={selectWinery}
            mapHandle={mapHandle}
          />
        </MapSuspense>
      </MapBoundary>

      {status === 'unavailable' && <MapUnavailable />}

      {/* THE CHROME MOVES AROUND THE PANEL RATHER THAN BEING COVERED BY IT.
          `map_config.ts` has said so since P1, and this header is where it was
          broken twice: once from `md` up, where the nav links sat behind the
          panel and collided with its Close link, and once on a phone, where
          the control that reopens the filters sat under the bottom sheet.

          From `md` the reservation is `md:pr-[25.5rem]`: the panel's 24rem, its
          0.75rem inset from the edge, and a 0.75rem gap. It is breakpoint-
          scoped because below `md` the panel is a bottom sheet and there is
          nothing to move around.

          Below `md` the header is BOUNDED instead: `bottom-[60dvh]` stops its
          box where the sheet may start. The bound belongs on the header rather
          than on anything inside it — it was once a `max-h` on the inner
          column, which silently assumed where that column started, and the day
          the nav wrapped above it the column reached 28px into the sheet.
          Whatever wraps in here, the box ends where the sheet begins.

          Five things in this markup are load-bearing, and none of them is
          visible to a test that performs no layout:

          1. **z-40, above the dock's z-30.** It was the other way round. The
             header and the panel no longer overlap, so the order matters only
             for the header's own sheets, which on a phone must cover the detail
             sheet rather than open underneath it.
          2. **`content-start`.** With `bottom-[60dvh]` the header has a fixed
             height, and a wrapping flex container stretches its lines to fill
             one: without this the nav row lands about 100px too low.
          3. **No ancestor of a popover may gain `transform`, `filter`,
             `backdrop-filter`, `contain` or `will-change`.** Below `md` the
             Layers and Filters surfaces are `position: fixed` and live inside
             this header, next to their buttons. `overflow-hidden` does not clip
             a fixed descendant — but any of those properties would make this
             header their containing block, and the sheet would be clipped to
             the top 40dvh. That is also why nothing here is blurred.
          4. **The bar is one row or two, decided, and never three.** The
             cluster takes what the row has (`flex-1`), the search field in it
             gives way down to 11rem, and the two buttons drop their words for
             their icons where a panel leaves the bar too little room
             (`chromeButtonLabelClass`). So with no panel it is one row from
             `md`. Beside an open panel it is one row from `lg`, and below
             that the phone's arrangement: the cluster takes the whole first
             row (`basis-full`) and the nav sits under it, left-aligned. It
             used to wrap wherever it ran out of room — at 1024 with a panel
             the nav alone on a second row, right-aligned against nothing; at
             768 three ragged rows — which read as overflow, and cost up to
             160px of map. The cluster is deliberately NOT a size container,
             which is how it would know its own width: that is containment,
             and rule 3 forbids it.
          5. **Nothing in here changes size in response to hover or loading.**
             The hover name is a tooltip at the pointer and the pending states
             are in the notice stack; both used to render in the nav and push
             its links sideways. */}
      <header
        className={`pointer-events-none absolute inset-x-0 top-0 z-40 flex flex-wrap content-start items-start gap-2 p-3 md:gap-3 md:p-4 ${
          panelOpen ? 'bottom-[60dvh] overflow-hidden md:bottom-auto md:overflow-visible md:pr-[25.5rem]' : ''
        }`}
      >
        {/* One row: the search pill, then the two buttons. `relative`, because
            each popover is positioned against this box from `md` up, and on a
            phone the search results are too. `basis-full` gives the row the
            whole width and sends the nav to a second one — below `md`, and
            below `lg` beside an open panel (rule 4 above). */}
        <div
          className={`relative flex min-w-0 max-w-full flex-1 basis-full items-start gap-2 ${
            panelOpen ? 'lg:basis-0' : 'md:basis-0'
          }`}
        >
          {/* In the chrome rather than on the map page, so it is present on `/`,
              `/places/:slug` and `/grapes/:slug` alike — search is a prop of
              whatever page the reader is on, never a route of its own. */}
          <SearchBox
            results={search}
            onSelectPlace={selectPlace}
            onSelectGrape={selectGrape}
            onSelectWinery={selectWinery}
            onIntent={intend}
          />

          <LayerToggles
            open={popover === 'layers'}
            onToggleOpen={(open) => setPopover(open ? 'layers' : null)}
            beside={panelOpen}
            journalLayers={signedIn}
            countriesVisible={visibility.countries}
            regionsVisible={visibility.regions}
            regionsEnabled={regionsEnabled}
            wineriesVisible={visibility.wineries}
            wineriesEnabled={wineriesEnabled}
            tastingsVisible={visibility.tastings}
            wishlistVisible={visibility.wishlist}
            tastingCount={my_tastings.features.length}
            wishlistCount={my_wishlist.features.length}
            unpinnedTastings={my_tastings.unpinned}
            onToggleCountries={(countries) => setVisibility((current) => ({ ...current, countries }))}
            onToggleRegions={(regions) => setVisibility((current) => ({ ...current, regions }))}
            onToggleWineries={(wineries) => setVisibility((current) => ({ ...current, wineries }))}
            onToggleTastings={(tastings) => setVisibility((current) => ({ ...current, tastings }))}
            onToggleWishlist={(wishlist) => setVisibility((current) => ({ ...current, wishlist }))}
          />

          {/* Beside the layers, collapsed at every width. Opening either
              closes the other: `popover` holds one name. */}
          <WineryFilters
            facets={winery_facets}
            filters={filters}
            open={popover === 'filters'}
            onToggleOpen={(open) => setPopover(open ? 'filters' : null)}
            onChange={setFilters}
            beside={panelOpen}
          />
        </div>

        {/* One pill, and only links. On a phone it is the header's second row,
            left-aligned under the search field; from `md` it sits at the right,
            and beside an open panel the header's reservation is what keeps it
            clear of the Close control. These stay real `<a>` elements with
            their words visible at every width: on the width most likely to be
            used at a dinner table, the journal is one tap from the map, not
            two behind a menu.

            A visitor has no journal, so they are offered Sources — the atlas's
            account of itself, which needs no account — and the way in. */}
        <nav
          aria-label="Sections"
          className="pointer-events-auto flex h-11 items-center rounded-full bg-surface-raised px-1 shadow-chrome ring-1 ring-black/5 md:h-10"
        >
          {signedIn && (
            <>
              <Link href={JOURNAL_PATH} className={`${NAV_LINK_CLASS} text-ink`}>
                Journal
              </Link>
              <Link href={WISHLIST_PATH} className={`${NAV_LINK_CLASS} text-ink-soft hover:text-ink`}>
                Wishlist
              </Link>
            </>
          )}
          {/* Offered to everyone: the stories need no account, and a visitor
              who came for the map is exactly who they were written for. */}
          <Link href={SPOTLIGHT_PATH} className={`${NAV_LINK_CLASS} text-ink-soft hover:text-ink`}>
            Spotlight
          </Link>
          <Link href={SOURCES_PATH} className={`${NAV_LINK_CLASS} text-ink-soft hover:text-ink`}>
            Sources
          </Link>
          {/* Back to where they were. A reader who signs in from Temecula is
              there to save something about Temecula, and the server cannot be
              relied on to remember the page for them: a panel answered from a
              prefetch is a page the server never saw this reader open. From
              the bare map there is nothing to return to but the default. */}
          {!signedIn && (
            <Link
              href={pagePath ? `${SIGN_IN_PATH}?return_to=${encodeURIComponent(pagePath)}` : SIGN_IN_PATH}
              className={`${NAV_LINK_CLASS} text-ink`}
            >
              Sign in
            </Link>
          )}
        </nav>
      </header>

      {/* For a screen reader only. A navigation is shown to everyone else by
          Inertia's own progress bar (coloured in `entrypoints/inertia.tsx`); a
          visible chip as well was a second indicator for one event, and it sat
          in the nav and moved the links. Not said while a stand-in panel is
          up: that panel has a status of its own, which names what is loading. */}
      {navigating && !awaited && (
        <p data-testid="atlas-pending" role="status" className="sr-only">
          Loading…
        </p>
      )}

      {/* THE DOCK: everything anchored to the bottom edge — the notices, the
          attribution, and the page's detail panel — as ONE column on a phone.

          That is what finally makes the attribution honour the rule in the
          header's comment. It used to be `absolute bottom-0` at z-30 under a
          z-40 panel with no reservation, so on a phone it was simply underneath
          the sheet, and on a desktop it showed through the panel as ghost text.
          `map_config.ts` says why that matters: the ODbL credit has to be
          visible. In a bottom-anchored flex column the credit and the notices
          ride ON TOP of the sheet, whatever height the sheet turns out to be —
          it is "up to" 60dvh, and France's is about 180px.

          From `md` the dock is `display: contents`: it generates no box, and
          its children position against the shell. The footer and the notice
          stack then move left of the panel with the same arithmetic as the
          header (`md:right-[24.75rem]` is the panel's 24rem plus its 0.75rem
          inset; the stack takes the header's `md:pr-[25.5rem]`).

          **What the dock asks of the panel.** Below `md` the `<aside>` must be
          IN FLOW here, as the column's last item — so the dock says so itself
          (`max-md:[&>aside]:relative`) rather than trusting every panel to,
          because an absolutely positioned sheet is out of the column and the
          credit lands under it again. It also restores the pointer events this
          box turns off for the sake of the map beneath it. From `md` the aside
          positions itself, `absolute` against the shell at the right edge. */}
      <div
        ref={dock}
        data-testid="atlas-dock"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-30 flex flex-col max-md:[&>aside]:relative md:contents [&>aside]:pointer-events-auto"
      >
        {/* THE BAND ALONG THE BOTTOM OF THE MAP: the notices, and under them
            the footer. ONE COLUMN AT EVERY WIDTH. On a phone it always was —
            it is the dock's flow. From `md` the two were each pinned to the
            bottom edge on their own, the notices at a fixed 36px: right for a
            one-line credit, and flush on top of a credit that had wrapped to
            two lines — "Loading wineries…" touched the attribution box, and a
            failure banner lay over its corner. In a column the notices sit on
            whatever the footer turns out to be, with a gap, and nobody has to
            know how tall that is.

            The column is what is positioned from `md` — against the shell,
            left of an open panel by the header's own arithmetic
            (`md:right-[24.75rem]` is the panel's 24rem plus its 0.75rem
            inset) — and the two things in it are simply in it. */}
        <div
          className={`flex flex-col md:absolute md:bottom-0 md:left-0 md:z-20 md:gap-2 ${
            panelOpen ? 'md:right-[24.75rem]' : 'md:right-0'
          }`}
        >
        {/* ONE stack for everything the map has to say about itself, centred on
            the part of the map that is not under the panel. These were four
            banners pinned at `top-20`, `top-32` and `top-44` of the full
            viewport: they collided with the chrome's left column, and beside
            an open panel they slid under it. A column cannot collide with
            itself. `empty:hidden` so an idle stack takes no space above the
            attribution.

            Failures are cards in the warning skin and come first; what the
            layer is doing or showing is a dark pill; a pending fetch is the
            same pill, held back 350ms by `animate-late-in` so a quick one
            announces nothing. None of them takes layout from the header.

            Below `md` the stack stands down while Layers or Filters is open.
            Their sheet rises from this same edge and is drawn above the dock,
            so a notice would otherwise show as a sliver cut off by the sheet's
            top corner; it is back the moment the sheet is put away.

            THE STACK KEEPS CLEAR OF THE LOCATE BUTTON, which rides above the
            attribution at the right-hand end of this same band (the footer
            below). On a phone that is its right padding: a notice is set in
            the width the button leaves, beside it rather than above it, which
            costs a line of wrapping and none of the map's height. From `md`
            the stack is centred on the visible map and a notice is at most
            28rem of it, so only a narrow window brings the two together — a
            tablet beside an open panel — and there the same 4rem on each side
            keeps a notice centred and off the button. */}
        <div
          ref={noticeStack}
          className={`flex flex-col items-center gap-2 pb-1 pl-3 pr-16 empty:hidden md:px-16 md:pb-0 ${
            popover ? 'max-md:hidden' : ''
          }`}
        >
          {regionFetch === 'failed' && (
            <div data-testid="regions-failed" role="alert" className={WARNING_NOTICE_CLASS}>
              <span className="flex-1">The appellations could not be loaded.</span>
              <button type="button" onClick={retryRegions} className={NOTICE_BUTTON_CLASS}>
                Retry
              </button>
              <button type="button" onClick={dismissRegionFailure} className={NOTICE_BUTTON_CLASS}>
                Dismiss
              </button>
            </div>
          )}

          {wineryFetch.state === 'failed' && (
            <div data-testid="wineries-failed" role="alert" className={WARNING_NOTICE_CLASS}>
              <span className="flex-1">The winery layer could not be loaded.</span>
              <button type="button" onClick={wineryFetch.retry} className={NOTICE_BUTTON_CLASS}>
                Retry
              </button>
              <button type="button" onClick={wineryFetch.dismiss} className={NOTICE_BUTTON_CLASS}>
                Dismiss
              </button>
            </div>
          )}

          {/* KTD10 promises the cap is honest about itself. Without this, dense
              regions like Bordeaux and the Mosel silently draw fewer producers
              than they hold, which is the "broken, not honest" failure arriving
              through the one door KTD11 does not watch. It has nothing to press,
              so it stays transparent to the pointer and the map under it can
              still be dragged. */}
          {wineriesVisible && wineryLayer?.truncated && (
            <p data-testid="wineries-truncated" role="status" className={`${NEUTRAL_NOTICE_CLASS} py-1.5 text-center`}>
              Showing {drawnWineries(wineryLayer).toLocaleString('en-US')} of{' '}
              {wineryLayer.total.toLocaleString('en-US')} wineries here — zoom in for the rest.
            </p>
          )}

          {noWineriesMatch && (
            <div
              data-testid="wineries-empty"
              role="status"
              className={`${NEUTRAL_NOTICE_CLASS} pointer-events-auto flex items-center gap-3 py-0.5`}
            >
              {/* "Here": the filter panel beside this counts the whole atlas,
                  and may be showing 217 matches for a filter this view has
                  none of. Without the word the two contradict each other. */}
              <span className="flex-1">No wineries here match these filters.</span>
              <button
                type="button"
                data-testid="wineries-empty-clear"
                onClick={() => setFilters(EMPTY_WINERY_FILTERS)}
                className={NOTICE_BUTTON_CLASS}
              >
                Clear filters
              </button>
            </div>
          )}

          {/* One pill for both layers, so a slow settle raises one thing and
              not two. Each layer keeps its own status inside it.

              Each half is held back by a timer of its own
              (`REGION_PENDING_DELAY_MS`, `WINERY_PENDING_DELAY_MS`), so a
              fetch that answers inside it mounts nothing at all; the pill
              then only fades. It used to lean on a CSS animation delay for
              the winery half, which is no delay at all for a reader who has
              asked for reduced motion. `router.reload` is an async visit and
              Inertia's progress bar never appears for one, which is why the
              layers announce themselves here at all. */}
          {(regionFetch === 'pending' || wineriesPending) && (
            <p className={`${NEUTRAL_NOTICE_CLASS} flex items-center gap-2 py-1.5 motion-safe:animate-content-in`}>
              {regionFetch === 'pending' && (
                <span data-testid="regions-pending" role="status">
                  Loading appellations…
                </span>
              )}
              {wineriesPending && (
                <span data-testid="wineries-pending" role="status">
                  Loading wineries…
                </span>
              )}
            </p>
          )}

          {/* And, last, whatever "Show my location" has to say when the browser
              would not or could not answer a press. It is not written here: the
              locate control adds it to this element from where it is mounted
              (`noticeStack`), so its coming and going renders that control and
              not the shell. */}
        </div>

        {/* With no sheet beneath it the credit is the last thing on a phone's
            screen, so it is what clears the home indicator.

            THE CREDIT IS THREE OBLIGATIONS — the basemap's, the relief's and
            the winery data's — AND IT GIVES THE KEY ROOM. The footer is a size
            container spanning the map that is not under the panel, and the
            credit asks it how wide it is. Three forms:

            - From 88rem of row (a 1440px window with no panel): ONE LINE, with
              the relief's credit in full — the dataset named, its largest
              contributors named. That line is 815px, and the row still has
              about 590 for the scale and the whole key.
            - From 72rem (a laptop with no panel open): ONE LINE, with the
              relief's credit SHORT — "Relief: Terrain Tiles", the name linked
              to the dataset's card on the sources page, where every statement
              its licence asks for is printed. About 620px, which leaves the
              key 500 or more. The list of contributors is the one thing the
              line drops, and only below 88rem; a short visible credit that
              links to the full statement is what the dataset's own attribution
              document calls best practice (docs/atlas/relief.md).
            - Below that: the same short wording as a compact block, a size
              smaller, no wider than 22.5rem, breaking only BETWEEN credits:
              two lines, right-aligned in the corner — the basemap's, then the
              relief's and the winery data's. That is a 1440px window beside a
              panel, where two short lines leave the key 650px and one line
              would leave it 400; and a phone, where it was three lines across
              the whole width.

            It was the full wording at every width: one line from 70rem and a
            block up to 36rem wide below it. Beside a panel on a 1440px window
            that block was 576px of a 1016px row, and the plate's key — which
            the direction this plate was chosen from shows saying what the
            amber line is — was down to a scale bar and one entry; at 1024px
            with a panel there was no key and no scale at all. Every word and
            every link the three licences ask for is still on the map at every
            width (see `BasemapAttribution`, which also has how the separators
            stay off the ends of a line), and it hugs the bottom-right corner
            of the visible map.

            OPAQUE PAPER, like every other label the chrome lays on the plate.
            It was 85% of it, and town names ghosted through the credit.

            The footer is a size container, so its width is not its content's:
            from `md` it fills the column it is in, and the column is what the
            shell positions. The box takes no clicks: it inherits
            `pointer-events: none` from the dock, and only the credit itself
            turns them back on.

            THE PLATE RIBBON SHARES THIS ROW, at its other end: the scale and
            the key for what is on screen. Being in the footer is what moves it
            for the panel and the sheet with no arithmetic of its own, and it
            asks the same container how wide the row is — it takes what the
            credit leaves, and where the row cannot hold both there is no
            ribbon (`PlateRibbon` has the rule). It is handed each layer as the
            map is DRAWING it, so a layer the reader switched off is not keyed,
            and nothing it reads changes while the camera is moving.

            AND THE LOCATE BUTTON RIDES ON TOP OF IT, at the credit's end:
            bottom right of the visible map, which is where a thumb is on a
            phone and the corner furthest from the search field and its two
            buttons on a desktop. It is positioned against this footer and
            just above it (`bottom-full`) rather than given a place in the
            row, for two reasons. The row's width is spoken for to the pixel —
            the credit's forms and the ribbon's fitting are both measured
            against it. And the credit is one or two lines tall depending on
            that width; a button that sits on the footer's top edge clears it
            at either height without knowing which. Being in the footer is
            also what moves it left of an open panel and lifts it above a
            phone's sheet, with no arithmetic of its own — which is why the
            footer is `relative`. Out of the flow, the button adds nothing to
            the dock's height, so the camera's bottom inset on a phone is what
            it was: a fitted place may run under a 44px circle in the corner
            sooner than every place be drawn 50px higher for it. */}
        <footer
          className={`@container relative flex items-end justify-end gap-2 px-2 pt-1 md:p-2 md:pl-4 ${
            panelOpen ? 'pb-1.5' : 'pb-[max(0.375rem,env(safe-area-inset-bottom))]'
          }`}
        >
          <Locate
            ready={status === 'ready' || status === 'basemap-error'}
            mapHandle={mapHandle}
            insets={locateInsets}
            plain={plainNow}
            home={openedBare}
            stack={noticeStack}
          />
          <PlateRibbon
            viewport={viewport}
            detailZoom={map_zoom.detail}
            worldLayer={(visibility.countries && world_layer) || null}
            regionLayer={visibility.regions ? regionLayer : null}
            wineryLayer={wineriesVisible ? wineryLayer : null}
            tastings={signedIn && visibility.tastings && my_tastings.features.length > 0}
            // What the map has drawn in the selection colour. An outline is
            // the larger mark and keys as one: the open place's, or the open
            // producer's appellation's — unless that place has no shape to
            // outline, or the reader has the appellations switched off. Then
            // a producer keys as its ringed dot, and a place as nothing,
            // because nothing on the map is amber and the key must not say
            // something is. A COUNTRY keys as the soft band the map draws for
            // one, not as an appellation's ribbon.
            selected={
              selectedSlug && (selectedWinerySlug ? visibility.regions : place?.slug !== selectedSlug || place.bbox)
                ? selectedIsCountry
                  ? 'country'
                  : 'place'
                : selectedWinerySlug
                  ? 'winery'
                  : null
            }
          />
          <BasemapAttribution
            testId="basemap-attribution"
            wineryData={wineriesVisible}
            align="end"
            tailClassName="hidden @[88rem]:inline"
            className="pointer-events-auto max-w-[min(100%,22.5rem)] shrink-0 rounded-lg bg-surface-raised px-2 py-1 text-[9px] leading-[1.35] text-ink-soft @[45rem]:text-[10px] @[72rem]:max-w-full @[72rem]:rounded-full @[72rem]:px-2.5 @[72rem]:leading-tight"
          />
        </footer>
        </div>

        {/* The panel: a side panel from md up, a bottom sheet below it, and
            the last item of this column either way.

            It is the page's own, unless the reader has asked for something
            the page is not yet — then it is the stand-in for where they are
            going, through the same `<aside>` in the same place, so the header
            and the attribution move once. And unless they have just closed it,
            in which case there is none while the visit home is in the air.

            The contexts are how a panel, mounted by Inertia and handed no
            props by this shell, still learns what the shell knows: whether it
            is arriving, when its frame began to, how to close, how a link
            inside it opens the next one, and whether the map beside it is
            filtered. */}
        <MapFilteredContext value={wineriesVisible && activeFilterCount(filters) > 0}>
        <PanelNavigationContext value={panelNavigation}>
          <PanelCloseContext value={closePanel}>
            <PanelEnteredAtContext value={shellSince.current}>
              <PanelEnteringContext value={entering}>
                {awaited ? (
                  <PendingPanel
                    target={awaited.target}
                    failed={awaited.failed}
                    held={awaited.held}
                    onRetry={retryPanel}
                    onDismiss={dismissPanel}
                  />
                ) : closing ? null : (
                  children
                )}
              </PanelEnteringContext>
            </PanelEnteredAtContext>
          </PanelCloseContext>
        </PanelNavigationContext>
        </MapFilteredContext>
      </div>

      <AtlasList world={world_layer} failed={mapFailed} replace={Boolean(place)} />

      {atlasIsEmpty && (
        <div
          data-testid="empty-atlas"
          className="absolute inset-0 z-20 grid place-items-center bg-surface/95 px-6 text-center"
        >
          <div className="flex max-w-md flex-col items-center gap-3 rounded-2xl bg-surface-raised p-8 shadow-panel ring-1 ring-black/5">
            <h2 className="font-display text-xl font-semibold tracking-tight">The atlas is empty</h2>
            <p className="text-sm text-ink-soft">Nothing has been imported yet. Run the importers, then reload:</p>
            <code className="rounded-lg bg-ink px-3 py-2 text-sm text-white">bin/rails import:all</code>
            <p className="text-xs leading-relaxed text-ink-soft">
              Or one source at a time: <code>bin/rails import:countries</code>, <code>bin/rails import:avas</code>,{' '}
              <code>bin/rails import:wine_knowledge</code>.
            </p>
          </div>
        </div>
      )}

      <HoverLabel ref={hoverLabel} />
    </div>
  )
}
